import type { QuickSetupFormState, WizardStep } from './types';

/** Single source of truth for wizard workflow — avoids step/submitState drift. */
export type WizardPhase =
  | 'category'
  | 'search'
  | 'recipient'
  | 'review'
  | 'creating'
  | 'success'
  | 'error'
  | 'duplicate';

export type WizardStepperState = {
  activeStep: WizardStep | null;
  completedSteps: ReadonlySet<WizardStep>;
};

const ORDERED_PHASES: WizardPhase[] = [
  'category',
  'search',
  'recipient',
  'review',
  'creating',
  'success',
];

export function phaseToStep(phase: WizardPhase): WizardStep {
  switch (phase) {
    case 'category':
      return 1;
    case 'search':
      return 2;
    case 'recipient':
      return 3;
    case 'review':
    case 'creating':
    case 'success':
    case 'error':
    case 'duplicate':
      return 4;
    default:
      return 1;
  }
}

export function deriveStepperState(phase: WizardPhase): WizardStepperState {
  if (phase === 'success') {
    return {
      activeStep: null,
      completedSteps: new Set<WizardStep>([1, 2, 3, 4]),
    };
  }

  const activeStep = phaseToStep(phase);
  const completedSteps = new Set<WizardStep>();
  for (let id = 1; id < activeStep; id += 1) {
    completedSteps.add(id as WizardStep);
  }
  return { activeStep, completedSteps };
}

export function nextPhase(phase: WizardPhase): WizardPhase | null {
  if (phase === 'category') return 'search';
  if (phase === 'search') return 'recipient';
  if (phase === 'recipient') return 'review';
  return null;
}

export function previousPhase(phase: WizardPhase): WizardPhase | null {
  if (phase === 'search') return 'category';
  if (phase === 'recipient') return 'search';
  if (phase === 'review') return 'recipient';
  if (phase === 'error') return 'review';
  if (phase === 'duplicate') return 'recipient';
  return null;
}

export function isWizardLocked(phase: WizardPhase): boolean {
  return phase === 'creating' || phase === 'success';
}

export function canSubmitWizard(phase: WizardPhase): boolean {
  return phase === 'review';
}

export type WizardPlanLimits = {
  enforcePlanLimits?: boolean;
  maxSourcesPerSearch?: number;
  accessibleSources?: number;
};

function validateSearchStep(form: QuickSetupFormState, limits?: WizardPlanLimits): string | null {
  if (!form.source_names.length) return 'Select at least one source';
  if (
    limits?.enforcePlanLimits !== false &&
    limits?.maxSourcesPerSearch != null &&
    form.source_names.length > limits.maxSourcesPerSearch
  ) {
    if (
      limits.accessibleSources != null &&
      limits.accessibleSources > limits.maxSourcesPerSearch
    ) {
      return `You can access ${limits.accessibleSources} sources. Your plan allows selecting up to ${limits.maxSourcesPerSearch} sources per search.`;
    }
    return `Your plan allows up to ${limits.maxSourcesPerSearch} sources per search.`;
  }
  switch (form.category) {
    case 'vehicles':
      if (!form.search_text.trim() && !form.brand.trim()) {
        return 'Enter search text or brand';
      }
      break;
    case 'real_estate':
      if (!form.search_text.trim() && !form.city.trim()) {
        return 'Enter search text or city';
      }
      break;
    case 'jobs':
      if (!form.keyword.trim()) return 'Enter a job keyword';
      break;
    case 'marketplace':
      if (!form.search_text.trim()) return 'Enter search text';
      break;
    case 'custom':
      if (!form.search_text.trim() && !form.custom_filters.trim()) {
        return 'Enter search text or extra filters';
      }
      break;
    default:
      break;
  }
  return null;
}

function validateRecipientStep(form: QuickSetupFormState): string | null {
  if (!form.telegram_chat_id.trim()) return 'Enter a Telegram recipient';
  if (!form.name.trim()) return 'Enter an alert name';
  return null;
}

export function validateBeforeAdvance(
  phase: WizardPhase,
  form: QuickSetupFormState,
  limits?: WizardPlanLimits,
): string | null {
  if (phase === 'search') return validateSearchStep(form, limits);
  if (phase === 'recipient') return validateRecipientStep(form);
  return null;
}

/** Validate entire form before creation. */
export function validateBeforeCreate(
  form: QuickSetupFormState,
  limits?: WizardPlanLimits,
): string | null {
  return validateRecipientStep(form) || validateSearchStep(form, limits);
}

export const CREATION_PROGRESS_STEPS = [
  'Creating search…',
  'Creating subscription…',
  'Linking recipient…',
  'Queueing scrape jobs…',
] as const;

export function initialWizardPhase(): WizardPhase {
  return ORDERED_PHASES[0];
}
